/**
 * Service proxy layer.
 * When mock mode is enabled (persisted in localStorage), returns mock service
 * functions. Otherwise returns real Firebase service functions.
 *
 * This approach avoids modifying every context/page file. Instead, consumers
 * import from this proxy, and the proxy decides which implementation to use.
 *
 * The check is done once at module load time (localStorage is synchronous).
 * When the user toggles mock mode, the page reloads, so modules re-evaluate.
 */

const STORAGE_KEY = 'fitsocial_mock_enabled';
const DEMO_MOCK_KEY = 'fitsocial_demo_mock';

/**
 * Check if mock mode is enabled.
 * Allowed in development (via toggle) or in any environment when the
 * demo account is active (localStorage flag set during demo login).
 * @returns {boolean}
 */
const isMockEnabled = () => {
  try {
    // Demo mock mode works in all environments (including production)
    if (localStorage.getItem(DEMO_MOCK_KEY) === 'true') return true;
    // Dev-only toggle
    if (process.env.NODE_ENV !== 'development') return false;
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const useMock = isMockEnabled();

// ── Posts ────────────────────────────────────────────────────────────────────
export const postsService = useMock
  ? require('./mock/mock-posts.service')
  : require('./posts.service');

// ── Users ────────────────────────────────────────────────────────────────────
export const usersService = useMock
  ? require('./mock/mock-users.service')
  : require('./users.service');

// ── Follow ───────────────────────────────────────────────────────────────────
export const followService = useMock
  ? require('./mock/mock-follow.service')
  : require('./follow.service');

// ── Notifications ────────────────────────────────────────────────────────────
export const notificationsService = useMock
  ? require('./mock/mock-notifications.service')
  : require('./notifications.service');

// ── Workouts ─────────────────────────────────────────────────────────────────
export const workoutsService = useMock
  ? require('./mock/mock-workouts.service')
  : require('./workouts.service');

// ── Personal Records ────────────────────────────────────────────────────────
export const personalRecordsService = useMock
  ? require('./mock/mock-personal-records.service')
  : require('./personal-records.service');

// ── Nutrition ────────────────────────────────────────────────────────────────
export const nutritionService = useMock
  ? require('./mock/mock-nutrition.service')
  : require('./nutrition.service');

// ── Accountability ───────────────────────────────────────────────────────────
export const accountabilityService = useMock
  ? require('./mock/mock-accountability.service')
  : require('./accountability.service');

// ── Goals ────────────────────────────────────────────────────────────────────
export const goalsService = useMock
  ? require('./mock/mock-goals.service')
  : require('./goals.service');

// ── Achievements ─────────────────────────────────────────────────────────────
export const achievementsService = useMock
  ? require('./mock/mock-achievements.service')
  : require('./achievements.service');

// ── Body Stats ───────────────────────────────────────────────────────────────
export const bodyStatsService = useMock
  ? require('./mock/mock-body-stats.service')
  : require('./body-stats.service');

// ── Comments ─────────────────────────────────────────────────────────────────
export const commentsService = useMock
  ? require('./mock/mock-comments.service')
  : require('./comments.service');

// ── Storage ──────────────────────────────────────────────────────────────────
export const storageService = useMock
  ? require('./mock/mock-storage.service')
  : require('./storage.service');

// ── Bookmarks ───────────────────────────────────────────────────────────────
export const bookmarksService = useMock
  ? require('./mock/mock-bookmarks.service')
  : require('./bookmarks.service');

// ── Settings ────────────────────────────────────────────────────────────────
export const settingsService = useMock
  ? require('./mock/mock-settings.service')
  : require('./settings.service');

// ── Search ──────────────────────────────────────────────────────────────────
export const searchService = useMock
  ? require('./mock/mock-search.service')
  : require('./search.service');

// ── Messages ────────────────────────────────────────────────────────────────
export const messagesService = useMock
  ? require('./mock/mock-messages.service')
  : require('./messages.service');

// Export the check so contexts can use it
export { useMock as isMockMode };
